import { useCallback } from "react";
import { type ApolloCache } from "@apollo/client";

import { useMutation } from "@apollo/client/react";

import {
  MarkAllNotificationsReadDocument,
  MarkNotificationsClearedDocument,
  MarkNotificationsReadDocument,
  MarkNotificationsUnclearedDocument,
  type NotificationRow,
} from "#app/lib/data";
import {
  insertIntoConnection,
  removeFromConnection,
} from "#app/lib/data/connection-cache-utils";

// Store field name filters for targeting the correct connection cache entry.
// offsetPagination(["showCleared"]) keys them as notifications:{"showCleared":false}
export const ALL_TAB = ['"showCleared":false'];
const CLEARED_TAB = ['"showCleared":true'];

// Shared helper: adjust the root-level notificationUnreadCount in the Apollo cache.
export function updateGlobalUnreadCount(cache: ApolloCache, delta: number) {
  cache.modify({
    fields: {
      notificationUnreadCount(existing: number = 0) {
        return Math.max(0, existing + delta);
      },
    },
  });
}

// Adjust the connection-level unreadCount inside a specific notifications connection.
export function updateConnectionUnreadCount(
  cache: ApolloCache,
  storeFieldNameIncludes: string[],
  delta: number,
) {
  cache.modify({
    fields: {
      notifications(existing: any, { storeFieldName }: any) {
        for (const substring of storeFieldNameIncludes) {
          if (!storeFieldName.includes(substring)) return existing;
        }
        return {
          ...existing,
          unreadCount: Math.max(0, (existing?.unreadCount ?? 0) + delta),
        };
      },
    },
  });
}

export function useInboxMutations() {
  const [markReadMutation] = useMutation(MarkNotificationsReadDocument);
  const [markClearedMutation] = useMutation(MarkNotificationsClearedDocument);
  const [markUnclearedMutation] = useMutation(MarkNotificationsUnclearedDocument);
  const [markAllReadMutation] = useMutation(MarkAllNotificationsReadDocument);

  const markRead = useCallback(
    async (items: NotificationRow[]) => {
      const unreadItems = items.filter((i) => !i.read);
      if (unreadItems.length === 0) return;

      await markReadMutation({
        variables: { ids: unreadItems.map((i) => i.id) },
        update(cache) {
          updateGlobalUnreadCount(cache, -unreadItems.length);
          updateConnectionUnreadCount(cache, ALL_TAB, -unreadItems.length);
        },
      });
    },
    [markReadMutation],
  );

  const markCleared = useCallback(
    async (items: NotificationRow[]) => {
      if (items.length === 0) return;

      const unreadCount = items.filter((i) => !i.read).length;

      await markClearedMutation({
        variables: { ids: items.map((i) => i.id) },
        update(cache) {
          for (const item of items) {
            const entity = { __typename: "Notification" as const, inboxId: item.inboxId };

            // Move ref from All → Cleared connection list
            removeFromConnection(cache, "notifications", entity, {
              storeFieldNameIncludes: ALL_TAB,
            });
            insertIntoConnection(cache, "notifications", "NotificationEdge", entity, {
              position: "start",
              storeFieldNameIncludes: CLEARED_TAB,
            });

            // Update the entity's cleared field in-place. `identify` answers
            // `undefined` for an object it cannot key, and the cache reads an
            // absent `id` as the ROOT — the edit would land on ROOT_QUERY.
            const cacheId = cache.identify(entity);
            if (cacheId) {
              cache.modify({
                id: cacheId,
                fields: {
                  cleared() {
                    return new Date().toISOString();
                  },
                },
              });
            }
          }

          if (unreadCount > 0) {
            updateGlobalUnreadCount(cache, -unreadCount);
            updateConnectionUnreadCount(cache, ALL_TAB, -unreadCount);
            updateConnectionUnreadCount(cache, CLEARED_TAB, unreadCount);
          }
        },
      });
    },
    [markClearedMutation],
  );

  const markUncleared = useCallback(
    async (items: NotificationRow[]) => {
      if (items.length === 0) return;

      const unreadCount = items.filter((i) => !i.read).length;

      await markUnclearedMutation({
        variables: { ids: items.map((i) => i.id) },
        update(cache) {
          for (const item of items) {
            const entity = { __typename: "Notification" as const, inboxId: item.inboxId };

            // Move ref from Cleared → All connection list
            removeFromConnection(cache, "notifications", entity, {
              storeFieldNameIncludes: CLEARED_TAB,
            });
            insertIntoConnection(cache, "notifications", "NotificationEdge", entity, {
              position: "start",
              storeFieldNameIncludes: ALL_TAB,
            });

            // Clear the cleared field. `identify` answers `undefined` for an
            // object it cannot key, and the cache reads an absent `id` as the
            // ROOT — the edit would land on ROOT_QUERY.
            const cacheId = cache.identify(entity);
            if (cacheId) {
              cache.modify({
                id: cacheId,
                fields: {
                  cleared() {
                    return null;
                  },
                },
              });
            }
          }

          if (unreadCount > 0) {
            updateGlobalUnreadCount(cache, unreadCount);
            updateConnectionUnreadCount(cache, CLEARED_TAB, -unreadCount);
            updateConnectionUnreadCount(cache, ALL_TAB, unreadCount);
          }
        },
      });
    },
    [markUnclearedMutation],
  );

  const markAllRead = useCallback(async () => {
    await markAllReadMutation({
      update(cache) {
        cache.modify({
          fields: {
            notificationUnreadCount() {
              return 0;
            },
          },
        });
        updateConnectionUnreadCount(cache, ALL_TAB, -Infinity);
      },
    });
  }, [markAllReadMutation]);

  return { markRead, markCleared, markUncleared, markAllRead };
}
